import { useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Plus, Trash2, Search, FileText, MessageSquare, Target } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  open: "bg-blue-50 text-blue-700",
  in_progress: "bg-amber-50 text-amber-700",
  completed: "bg-emerald-50 text-emerald-700",
  closed: "bg-gray-100 text-gray-600",
};

export const CasesPanel = ({ getAuthHeader, participants = [], onRefresh }) => {
  const [cases, setCases] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [expandedCase, setExpandedCase] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [goalText, setGoalText] = useState("");
  const [form, setForm] = useState({
    participant_id: "", program: "", assigned_staff: "", start_date: "", target_end_date: "", notes: "",
  });

  const fetchCases = async () => {
    try {
      const res = await axios.get(`${API}/admin/cases`, getAuthHeader());
      setCases(res.data);
      setLoaded(true);
    } catch { toast.error("Failed to load cases"); }
  };

  if (!loaded) fetchCases();

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/cases`, form, getAuthHeader());
      toast.success("Case created");
      setShowForm(false);
      setForm({ participant_id: "", program: "", assigned_staff: "", start_date: "", target_end_date: "", notes: "" });
      fetchCases();
      if (onRefresh) onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to create case"); }
  };

  const handleStatusChange = async (caseId, newStatus) => {
    try {
      await axios.put(`${API}/admin/cases/${caseId}`, { status: newStatus }, getAuthHeader());
      toast.success("Status updated");
      fetchCases();
    } catch { toast.error("Failed to update"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this case?")) return;
    try {
      await axios.delete(`${API}/admin/cases/${id}`, getAuthHeader());
      toast.success("Deleted");
      fetchCases();
    } catch { toast.error("Failed to delete"); }
  };

  const handleAddNote = async (caseId) => {
    if (!noteText.trim()) return;
    try {
      await axios.post(`${API}/admin/cases/${caseId}/notes`, { content: noteText }, getAuthHeader());
      toast.success("Note added");
      setNoteText("");
      fetchCases();
    } catch { toast.error("Failed to add note"); }
  };

  const handleAddGoal = async (caseId) => {
    if (!goalText.trim()) return;
    try {
      await axios.post(`${API}/admin/cases/${caseId}/goals`, { description: goalText }, getAuthHeader());
      toast.success("Goal added");
      setGoalText("");
      fetchCases();
    } catch { toast.error("Failed to add goal"); }
  };

  const handleToggleGoal = async (caseId, goalId) => {
    try {
      await axios.put(`${API}/admin/cases/${caseId}/goals/${goalId}`, {}, getAuthHeader());
      fetchCases();
    } catch { toast.error("Failed to update goal"); }
  };

  const filtered = cases.filter(c =>
    `${c.case_number} ${c.participant_name} ${c.program}`.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input data-testid="case-search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search cases..." className="pl-10" />
        </div>
        <Button data-testid="add-case-btn" onClick={() => setShowForm(!showForm)} className="bg-warm-700 hover:bg-warm-800 text-white gap-2">
          <Plus className="w-4 h-4" />{showForm ? "Cancel" : "New Case"}
        </Button>
      </div>

      {showForm && (
        <form onSubmit={handleCreate} className="bg-gray-50 rounded-xl p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Participant *</Label>
            <select value={form.participant_id} onChange={e => setForm({ ...form, participant_id: e.target.value })} required className="mt-1 w-full border rounded-lg px-3 py-2 text-sm">
              <option value="">Select participant...</option>
              {participants.map(p => (
                <option key={p.id} value={p.id}>{p.first_name} {p.last_name}</option>
              ))}
            </select>
          </div>
          <div>
            <Label className="text-xs">Program *</Label>
            <select value={form.program} onChange={e => setForm({ ...form, program: e.target.value })} required className="mt-1 w-full border rounded-lg px-3 py-2 text-sm">
              <option value="">Select...</option><option value="Parenting Support Program">Parenting Support Program</option><option value="Violence Intervention">Violence Intervention</option><option value="Family Counseling">Family Counseling</option><option value="Youth Mentorship">Youth Mentorship</option>
            </select>
          </div>
          <div><Label className="text-xs">Assigned Staff</Label><Input value={form.assigned_staff} onChange={e => setForm({ ...form, assigned_staff: e.target.value })} className="mt-1" /></div>
          <div><Label className="text-xs">Start Date *</Label><Input type="date" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} required className="mt-1" /></div>
          <div><Label className="text-xs">Target End Date</Label><Input type="date" value={form.target_end_date} onChange={e => setForm({ ...form, target_end_date: e.target.value })} className="mt-1" /></div>
          <div className="sm:col-span-2"><Label className="text-xs">Initial Notes</Label><textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} className="mt-1 w-full border rounded-lg px-3 py-2 text-sm" /></div>
          <div><Button type="submit" className="bg-warm-700 hover:bg-warm-800 text-white">Create Case</Button></div>
        </form>
      )}

      <div className="space-y-3">
        {filtered.map(c => (
          <div key={c.id} className="bg-white border border-gray-100 rounded-xl overflow-hidden">
            <div className="px-5 py-4 flex items-center justify-between cursor-pointer hover:bg-gray-50/50" onClick={() => setExpandedCase(expandedCase === c.id ? null : c.id)}>
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-gray-900">{c.case_number}</span>
                  <span className="text-sm text-gray-500">- {c.participant_name}</span>
                  <select value={c.status} onChange={e => { e.stopPropagation(); handleStatusChange(c.id, e.target.value); }} className={`text-xs px-2 py-0.5 rounded-full font-medium border-0 cursor-pointer ${statusColors[c.status]}`} onClick={e => e.stopPropagation()}>
                    <option value="open">Open</option><option value="in_progress">In Progress</option><option value="completed">Completed</option><option value="closed">Closed</option>
                  </select>
                </div>
                <p className="text-xs text-gray-400 mt-1">{c.program} | Started: {c.start_date} {c.assigned_staff ? `| Staff: ${c.assigned_staff}` : ""}</p>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleDelete(c.id); }} className="h-8 w-8 p-0 text-red-500"><Trash2 className="w-4 h-4" /></Button>
              </div>
            </div>

            {expandedCase === c.id && (
              <div className="border-t border-gray-100 px-5 py-4 space-y-4 bg-gray-50/30">
                {/* Notes */}
                <div>
                  <h4 className="text-sm font-medium text-gray-700 flex items-center gap-1 mb-2"><MessageSquare className="w-4 h-4" />Case Notes</h4>
                  <div className="space-y-2 mb-3 max-h-48 overflow-y-auto">
                    {(c.notes || []).map((n, i) => (
                      <div key={n.id || i} className="bg-white p-3 rounded-lg text-sm">
                        <p className="text-gray-900">{n.content}</p>
                        <p className="text-xs text-gray-400 mt-1">{n.author} - {new Date(n.date).toLocaleDateString()}</p>
                      </div>
                    ))}
                    {(!c.notes || c.notes.length === 0) && <p className="text-xs text-gray-400">No notes yet</p>}
                  </div>
                  <div className="flex gap-2">
                    <Input value={noteText} onChange={e => setNoteText(e.target.value)} placeholder="Add a note..." className="flex-1" />
                    <Button size="sm" onClick={() => handleAddNote(c.id)} className="bg-warm-700 hover:bg-warm-800 text-white">Add</Button>
                  </div>
                </div>

                {/* Goals */}
                <div>
                  <h4 className="text-sm font-medium text-gray-700 flex items-center gap-1 mb-2"><Target className="w-4 h-4" />Goals</h4>
                  <div className="space-y-2 mb-3">
                    {(c.goals || []).map((g, i) => (
                      <div key={g.id || i} className="flex items-center gap-2 bg-white p-2 rounded-lg">
                        <button onClick={() => handleToggleGoal(c.id, g.id)} className={`w-5 h-5 rounded border flex-shrink-0 flex items-center justify-center ${g.status === "completed" ? "bg-emerald-500 border-emerald-500 text-white" : "border-gray-300"}`}>
                          {g.status === "completed" && <span className="text-xs">&#10003;</span>}
                        </button>
                        <span className={`text-sm flex-1 ${g.status === "completed" ? "line-through text-gray-400" : "text-gray-900"}`}>{g.description}</span>
                      </div>
                    ))}
                    {(!c.goals || c.goals.length === 0) && <p className="text-xs text-gray-400">No goals set</p>}
                  </div>
                  <div className="flex gap-2">
                    <Input value={goalText} onChange={e => setGoalText(e.target.value)} placeholder="Add a goal..." className="flex-1" />
                    <Button size="sm" onClick={() => handleAddGoal(c.id)} className="bg-warm-700 hover:bg-warm-800 text-white">Add</Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
        {filtered.length === 0 && <div className="py-8 text-center text-gray-400 text-sm">No cases found</div>}
      </div>
    </div>
  );
};
